import { useEffect } from 'react';
import { useRouter } from 'next/router';

// Types
import type { AxiosError } from 'axios';
import { stringify } from 'qs';
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';
// Hooks
import useUrlUserHomepage from '@/utils/hooks/user/useUrlUserHomepage';

// Utils
import { get } from '@/utils/client';

import type { DashboardType } from '@/types/dashboardType';
import type { SocialProfileType } from '@/types/socialProfileType';

import { GET_DASHBOARD_LIST } from '@/routes/apiEndpoints';

type DashboardsProps = {
    data: DashboardType[];
    cursor: string;
};

type filtersStateType = {
    name: string;
    social_profile: SocialProfileType['id'];
    group: SpGroupType['id'];
};

type getKeyProps = (
    pageIndex: number,
    previousPageData: DashboardsProps | null,
    params: filtersStateType
) => string | null;

const getKey: getKeyProps = (pageIndex, previousPageData, queryParams) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // Add the cursor to the API endpoint
    return `${GET_DASHBOARD_LIST}${stringify(
        {
            ...queryParams,
            cursor: previousPageData?.cursor || undefined
        },
        { addQueryPrefix: true }
    )}`;
};

type OptionsConfiguration = SWRInfiniteConfiguration<DashboardsProps, AxiosError<Error>>;

const useDashboards = (params: filtersStateType, options: OptionsConfiguration) => {
    const { userHomepage } = useUrlUserHomepage();

    const {
        data: dashboards,
        mutate: mutateDashboards,
        error: dashboardError,
        setSize,
        size
    } = useSWRInfinite<DashboardsProps, AxiosError<Error>>(
        (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, params),
        (url) => get(url).then((res) => res.data),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const router = useRouter();

    useEffect(() => {
        if (!dashboards && !dashboardError) {
            return;
        }

        if (dashboardError?.status === 404 || dashboardError?.status === 403) {
            router.push(userHomepage);
        }
    }, [dashboardError, dashboards]);

    const isLoadingInitialData = !dashboards && !dashboardError;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && dashboards && typeof dashboards[size - 1] === 'undefined');

    return {
        dashboards: dashboards?.flatMap((item) => item.data),
        mutateDashboards,
        dashboardError,
        hasLoadMore: dashboards && typeof dashboards[dashboards.length - 1].cursor === 'string', // Check if there is a cursor to activate the load more button
        loadMore: () => setSize((size) => size + 1),
        isLoadingMore,
        isLoading:
            !dashboards &&
            (!dashboardError || dashboardError?.status === 404 || dashboardError?.status === 403)
    };
};
export default useDashboards;
